import {ParseResult, Schema} from "effect";

// ── Bare document UID (unbranded UUID) ──────────────────────────────────

/**
 * A bare document UID is the raw 36-character UUID with no principal prefix.
 * It is the form used in Firestore document paths (`documents/{uid}`) — the
 * persistence-layer sink type.
 *
 * Branded so a {@link PrefixedProjectUid} (`prj_…`) or a plain `string` cannot
 * be assigned where a bare document UID is required; mismatches surface at
 * compile time instead of as silent string inequality.
 */
const BareDocumentUidSchema = Schema.String.pipe(Schema.length(36), Schema.brand("BareDocumentUid"));

export type BareDocumentUid = typeof BareDocumentUidSchema.Type;

/**
 * Type guard — decides whether a value is a BareDocumentUid.
 */
export const isBareDocumentUid = Schema.is(BareDocumentUidSchema);

// ── Prefixed project UID (new canonical form) ──────────────────────────

/**
 * A prefixed project UID is a string that starts with "prj_" followed by the
 * bare document UUID.
 *
 * Effect Schema equivalent of the legacy zod `ProjectUid` declared in
 * `@buildwithflux/identifiers-schema/legacy`. This is the canonical wire form
 * for document/project identifiers crossing API boundaries. Unlike the loose
 * legacy `startsWith("prj_")` schema, the suffix must be a full 36-character
 * bare document UUID — a bare `"prj_"` or truncated uid fails decoding.
 */
const PrefixedProjectUidSchema = Schema.TemplateLiteral("prj_", Schema.String).pipe(
    Schema.filter((value) => isBareDocumentUid(value.slice("prj_".length)), {
        description: "a prj_-prefixed 36-character document UUID",
    }),
    Schema.brand("PrefixedProjectUid"),
);

export type PrefixedProjectUid = typeof PrefixedProjectUidSchema.Type;

/**
 * Type guard — decides whether a string is a branded PrefixedProjectUid.
 */
export const isPrefixedProjectUid = Schema.is(PrefixedProjectUidSchema);

// ── Canonical inverse pair: Bare ↔ Prefixed ────────────────────────────

/**
 * `Schema.transformOrFail` bundling the inverse pair
 * `BareDocumentUid ↔ PrefixedProjectUid`.
 *
 * - decode (Bare → Prefixed): prepend `"prj_"` to a `BareDocumentUid`. Always
 *   succeeds for any value the source schema accepts.
 * - encode (Prefixed → Bare): strip the `"prj_"` prefix from a
 *   `PrefixedProjectUid`. The stripped value must satisfy `BareDocumentUid`'s
 *   `length(36)` invariant; if it does not, the transform fails with a typed
 *   `ParseResult.Type` issue.
 */
export const BareDocumentUidToPrefixedProjectUid = Schema.transformOrFail(
    BareDocumentUidSchema,
    PrefixedProjectUidSchema,
    {
        strict: true,
        decode: (bare) => ParseResult.succeed(`prj_${bare}` as PrefixedProjectUid),
        encode: (prefixed, options, ast) => {
            const stripped = prefixed.slice("prj_".length);
            // Delegate to the source schema's decoder so any future constraint
            // added to `BareDocumentUidSchema` is automatically enforced on the
            // encode direction without duplication.
            return ParseResult.mapError(
                Schema.decode(BareDocumentUidSchema)(stripped, options),
                (issue) =>
                    new ParseResult.Type(
                        ast,
                        prefixed,
                        `Stripped UID "${stripped}" does not satisfy BareDocumentUid: ${issue.message}`,
                    ),
            );
        },
    },
);

// ── Companion objects ──────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const BareDocumentUid = {
    schema: BareDocumentUidSchema,
    isBareDocumentUid,
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PrefixedProjectUid = {
    schema: PrefixedProjectUidSchema,
    isPrefixedProjectUid,
    /**
     * Canonical strict inverse — see {@link BareDocumentUidToPrefixedProjectUid}.
     */
    fromBare: BareDocumentUidToPrefixedProjectUid,
} as const;
